import React, { useState } from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Input, 
  Button, 
  useColorModeValue, 
  useToast, 
  Icon, 
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import { FiMail, FiMessageCircle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { API } from '../utils/apiBase';

export default function Footer() {
  const bg = useColorModeValue('white', 'gray.900');
  const textColor = useColorModeValue('gray.600', 'white');
  const borderTop = useColorModeValue('1px solid', 'none');
  const borderColor = useColorModeValue('gray.200', 'transparent');
  const inputBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const inputColor = useColorModeValue('gray.800', 'white');
  const tabBg = useColorModeValue('gray.100', 'gray.800');
  const activeTabBg = useColorModeValue('white', 'gray.700');
  const activeTabColor = useColorModeValue('blue.600', 'blue.300');
  const tabTextColor = useColorModeValue('gray.600', 'gray.400');

  const [email, setEmail] = useState('');
  const [phoneCode, setPhoneCode] = useState('+504');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleSubscribeEmail = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ status: 'warning', title: 'Por favor ingresa un correo válido.' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        toast({ status: 'success', title: '¡Gracias por suscribirte!', description: 'Recibirás nuestro boletín semanal por correo.' });
        setEmail('');
      } else {
        throw new Error(data.error || 'Error al suscribirse');
      }
    } catch (error) {
      toast({ status: 'error', title: 'Ocurrió un error', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeWhatsApp = async (e) => {
    e.preventDefault();
    if (!phoneNumber || phoneNumber.length < 6) {
      toast({ status: 'warning', title: 'Por favor ingresa un número de teléfono válido.' });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/whatsapp-subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_code: phoneCode, phone_number: phoneNumber })
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        toast({ status: 'success', title: '¡Número guardado!', description: 'Pronto empezaremos a enviar notificaciones por WhatsApp.' });
        setPhoneNumber('');
      } else {
        throw new Error(data.error || 'Error al suscribirse');
      }
    } catch (error) {
      toast({ status: 'error', title: 'Ocurrió un error', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box bg={bg} borderTop={borderTop} borderColor={borderColor} boxShadow={useColorModeValue('none', 'md')} py={8}>
      <Flex direction={{ base: 'column', md: 'row' }} justify="center" align="flex-start" maxW="4xl" mx="auto" px={4} gap={{ base: 8, md: 16 }}>
        
        {/* Newsletter Section */}
        <Box textAlign={{ base: 'center', md: 'left' }} flex="1" w="full">
          <Text fontWeight="bold" fontSize="lg" color={useColorModeValue('brand.800', 'brand.200')} mb={2}>
            Suscripciones
          </Text>
          <Text fontSize="sm" color={textColor} mb={4}>
            Elige cómo prefieres recibir nuestras actualizaciones (devocionales, eventos y más).
          </Text>
          
          <Tabs variant="unstyled" size="sm" isFitted>
            <TabList mb="1em" maxW="450px" mx={{ base: 'auto', md: '0' }} bg={tabBg} borderRadius="full" p={1}>
              <Tab 
                gap={2} 
                borderRadius="full" 
                color={tabTextColor}
                _selected={{ bg: activeTabBg, color: activeTabColor, boxShadow: 'sm', fontWeight: 'bold' }}
              >
                <Icon as={FiMail} /> Correo
              </Tab>
              <Tab 
                gap={2} 
                borderRadius="full" 
                color={tabTextColor}
                _selected={{ bg: activeTabBg, color: 'whatsapp.500', boxShadow: 'sm', fontWeight: 'bold' }}
              >
                <Icon as={FaWhatsapp} /> WhatsApp
              </Tab>
            </TabList>
            <TabPanels>
              
              {/* Tab: Email */}
              <TabPanel p={0} pt={4}>
                <form onSubmit={handleSubscribeEmail}>
                  <Flex w="full" maxW="450px" mx={{ base: 'auto', md: '0' }} direction={{ base: 'column', md: 'row' }} gap={2}>
                    <Input
                      size="lg"
                      h="48px"
                      placeholder="tu@correo.com"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      bg={inputBg}
                      color={inputColor}
                      borderColor={useColorModeValue('brand.200', 'whiteAlpha.300')}
                      _focus={{ borderColor: 'accent.500', boxShadow: 'none' }}
                      flex="1"
                    />
                    <Button 
                      size="lg"
                      h="48px"
                      type="submit" 
                      colorScheme="blue" 
                      bg="accent.500" 
                      _hover={{ bg: 'accent.600' }}
                      isLoading={loading}
                      px={6}
                      w={{ base: "100%", md: "auto" }}
                      flexShrink={0}
                    >
                      Suscribirme
                    </Button>
                  </Flex>
                </form>
              </TabPanel>

              {/* Tab: WhatsApp */}
              <TabPanel p={0} pt={4}>
                <form onSubmit={handleSubscribeWhatsApp}>
                  <Flex w="full" maxW="450px" mx={{ base: 'auto', md: '0' }} direction={{ base: 'column', md: 'row' }} gap={2}>
                    <Flex gap={2} flex="1">
                      <Select 
                        size="lg"
                        h="48px"
                        w="120px"
                        flexShrink={0}
                        value={phoneCode} 
                        onChange={(e) => setPhoneCode(e.target.value)}
                        bg={inputBg}
                        color={inputColor}
                        borderColor={useColorModeValue('brand.200', 'whiteAlpha.300')}
                      >
                        <option value="+504">🇭🇳 +504</option>
                        <option value="+502">🇬🇹 +502</option>
                        <option value="+503">🇸🇻 +503</option>
                        <option value="+505">🇳🇮 +505</option>
                        <option value="+506">🇨🇷 +506</option>
                        <option value="+507">🇵🇦 +507</option>
                        <option value="+52">🇲🇽 +52</option>
                        <option value="+1">🇺🇸 +1</option>
                      </Select>
                      <Input
                        size="lg"
                        h="48px"
                        placeholder="Número"
                        type="tel"
                        required
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        bg={inputBg}
                        color={inputColor}
                        borderColor={useColorModeValue('brand.200', 'whiteAlpha.300')}
                        _focus={{ borderColor: 'green.500', boxShadow: 'none' }}
                        flex="1"
                        minW="0"
                      />
                    </Flex>
                    <Button 
                      size="lg"
                      h="48px"
                      type="submit" 
                      bg="green.500"
                      color="white"
                      _hover={{ bg: 'green.600' }}
                      isLoading={loading}
                      px={6}
                      w={{ base: "100%", md: "auto" }}
                      flexShrink={0}
                    >
                      Suscribirme
                    </Button>
                  </Flex>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Al suscribirte, aceptas recibir notificaciones en el futuro por WhatsApp.
                  </Text>
                </form>
              </TabPanel>

            </TabPanels>
          </Tabs>
        </Box>

        {/* Copyright Section */}
        <Box textAlign={{ base: 'center', md: 'right' }} flex="1" mt={{ base: 4, md: 8 }}>
          <Text fontSize="sm" color={textColor}>
            © {new Date().getFullYear()} Sala Evangélica, Barrio Lempira. <br/>
            Todos los derechos reservados.
          </Text>
        </Box>
      </Flex>
    </Box>
  );
}
